import {
  AI_INPUT_MAX_EDGE,
  AI_INPUT_SKIP_BYTES,
  AI_JPEG_QUALITY,
  CLASSIFY_MAX_PIXELS,
} from "../config/tools/aiInputLimits";

/** One encoded file per source File and cap, so later AI calls reuse it. */
const preparedCache = new WeakMap();

/**
 * Fitted size that keeps aspect ratio and caps the long edge.
 * @returns {{ width: number, height: number, scale: number }}
 */
/** Longest side that keeps this aspect ratio inside a pixel budget. */
export function longEdgeForPixelCap(width, height, maxPixels) {
  const longEdge = Math.max(width, height, 1);
  const pixels = Math.max(width, 1) * Math.max(height, 1);
  if (!maxPixels || pixels <= maxPixels) return longEdge;
  return Math.max(1, Math.floor(longEdge * Math.sqrt(maxPixels / pixels)));
}

export function aiTargetSize(width, height, maxEdge = AI_INPUT_MAX_EDGE) {
  const longEdge = Math.max(width, height);
  if (!longEdge || longEdge <= maxEdge) {
    return { width, height, scale: 1 };
  }
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

/** PNG keeps alpha (png and webp sources). Everything else is JPEG. */
export function aiOutputType(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (
    type === "image/png" ||
    type === "image/webp" ||
    name.endsWith(".png") ||
    name.endsWith(".webp")
  ) {
    return "image/png";
  }
  return "image/jpeg";
}

function withExtension(name, mime) {
  const base = String(name || "image").replace(/\.[^.]+$/, "");
  return `${base}.${mime === "image/png" ? "png" : "jpg"}`;
}

function u16(bytes, offset, littleEndian) {
  if (littleEndian) return bytes[offset] | (bytes[offset + 1] << 8);
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function u32(bytes, offset, littleEndian) {
  if (littleEndian) {
    return (
      (bytes[offset] |
        (bytes[offset + 1] << 8) |
        (bytes[offset + 2] << 16) |
        (bytes[offset + 3] << 24)) >>>
      0
    );
  }
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  );
}

function readExifOrientation(bytes, start, length) {
  const sig = [0x45, 0x78, 0x69, 0x66, 0, 0];
  for (let i = 0; i < sig.length; i += 1) {
    if (bytes[start + i] !== sig[i]) return null;
  }
  const tiff = start + 6;
  const littleEndian = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
  if (u16(bytes, tiff + 2, littleEndian) !== 42) return null;
  const ifdOffset = u32(bytes, tiff + 4, littleEndian);
  let ifd = tiff + ifdOffset;
  const end = start + length;
  if (ifd + 2 > end) return null;
  const count = u16(bytes, ifd, littleEndian);
  ifd += 2;
  for (let i = 0; i < count; i += 1) {
    const entry = ifd + i * 12;
    if (entry + 12 > bytes.length) break;
    if (u16(bytes, entry, littleEndian) === 0x0112) {
      return u16(bytes, entry + 8, littleEndian);
    }
  }
  return null;
}

function isSof(marker) {
  return (
    marker >= 0xc0 &&
    marker <= 0xcf &&
    marker !== 0xc4 &&
    marker !== 0xc8 &&
    marker !== 0xcc
  );
}

/**
 * Width, height, and EXIF orientation from the file header. Null when unknown.
 * JPEG orientation 5–8 swaps the displayed axes.
 */
export function parseImageHeader(bytes) {
  if (!bytes || bytes.length < 8) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    if (bytes.length < 24) return null;
    return {
      width: u32(bytes, 16, false),
      height: u32(bytes, 20, false),
      orientation: 1,
    };
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    let orientation = 1;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) return null;
      const marker = bytes[offset + 1];
      if (marker === 0xd8 || marker === 0xd9) {
        offset += 2;
        continue;
      }
      const length = u16(bytes, offset + 2, false);
      if (length < 2) return null;
      if (marker === 0xe1) {
        const found = readExifOrientation(bytes, offset + 4, length - 2);
        if (found) orientation = found;
      }
      if (isSof(marker)) {
        return {
          height: u16(bytes, offset + 5, false),
          width: u16(bytes, offset + 7, false),
          orientation,
        };
      }
      offset += 2 + length;
    }
  }
  return null;
}

export function orientedSize(header) {
  if (!header) return null;
  const swap = header.orientation >= 5 && header.orientation <= 8;
  return swap
    ? { width: header.height, height: header.width }
    : { width: header.width, height: header.height };
}

function canvasToBlob(canvas, mime) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not encode image"))),
      mime,
      mime === "image/jpeg" ? AI_JPEG_QUALITY : undefined
    );
  });
}

function readBlob(blob) {
  if (typeof blob.arrayBuffer === "function") {
    try {
      const result = blob.arrayBuffer();
      if (result && typeof result.then === "function") return result;
    } catch (err) {
      // jsdom blobs expose arrayBuffer without implementing it.
    }
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

async function readHeader(file) {
  const buffer = await readBlob(file.slice(0, 128 * 1024));
  return parseImageHeader(new Uint8Array(buffer));
}

/**
 * Downscale a phone photo before it is sent to an AI endpoint.
 * Files already within the edge and byte caps are returned unchanged.
 * The same source file and cap reuse one encoded result.
 * The library upload should keep the original file.
 */
export async function prepareImageForAi(file, options = {}) {
  if (!file || typeof file.slice !== "function" || !file.size) return file;
  const display = orientedSize(await readHeader(file));
  const maxEdge = options.maxPixels
    ? display
      ? longEdgeForPixelCap(display.width, display.height, options.maxPixels)
      : Math.floor(Math.sqrt(options.maxPixels))
    : options.maxEdge || AI_INPUT_MAX_EDGE;
  const cacheKey = options.maxPixels
    ? `px:${options.maxPixels}`
    : `edge:${maxEdge}`;
  let byCap = preparedCache.get(file);
  if (!byCap) {
    byCap = new Map();
    preparedCache.set(file, byCap);
  }
  const cached = byCap.get(cacheKey);
  if (cached) return cached;

  const pending = encodeForCap(file, display, maxEdge, options.skipBytes);
  byCap.set(cacheKey, pending);
  try {
    return await pending;
  } catch (err) {
    byCap.delete(cacheKey);
    throw err;
  }
}

/** Classify cap is tighter than the shared working copy, so it gets its own size. */
export function prepareImageForClassify(file) {
  return prepareImageForAi(file, { maxPixels: CLASSIFY_MAX_PIXELS });
}

async function encodeForCap(file, display, maxEdge, skipBytesOption) {
  const skipBytes = skipBytesOption || AI_INPUT_SKIP_BYTES;

  const withinCap =
    display &&
    Math.max(display.width, display.height) <= maxEdge &&
    file.size <= skipBytes;
  if (withinCap) return file;

  const target = display ? aiTargetSize(display.width, display.height, maxEdge) : null;
  const bitmapOptions = { imageOrientation: "from-image" };
  if (target && target.scale < 1) {
    bitmapOptions.resizeWidth = target.width;
    bitmapOptions.resizeHeight = target.height;
    bitmapOptions.resizeQuality = "high";
  }

  const bitmap = await createImageBitmap(file, bitmapOptions);
  try {
    const fitted = aiTargetSize(bitmap.width, bitmap.height, maxEdge);
    const unchanged =
      fitted.scale === 1 && file.size <= skipBytes && bitmap.width === (display?.width || bitmap.width);
    if (unchanged) return file;

    const canvas = document.createElement("canvas");
    canvas.width = fitted.width;
    canvas.height = fitted.height;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, fitted.width, fitted.height);
    const mime = aiOutputType(file);
    const blob = await canvasToBlob(canvas, mime);
    return new File([blob], withExtension(file.name, mime), {
      type: mime,
      lastModified: file.lastModified || Date.now(),
    });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Nearest-neighbor resize so a painted mask matches the fitted AI image.
 */
export async function alignMaskToImage(maskFile, imageFile) {
  if (!maskFile || !imageFile) return maskFile;
  const [mask, image] = await Promise.all([
    createImageBitmap(maskFile),
    createImageBitmap(imageFile, { imageOrientation: "from-image" }),
  ]);
  try {
    if (mask.width === image.width && mask.height === image.height) return maskFile;
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(mask, 0, 0, image.width, image.height);
    const blob = await canvasToBlob(canvas, "image/png");
    const name = withExtension(maskFile.name || "mask.png", "image/png");
    return new File([blob], name, { type: "image/png" });
  } finally {
    mask.close?.();
    image.close?.();
  }
}
